import React from 'react';

import { useListAccountGroupProfilesWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useListCashflowConfigsForAccountGroups } from 'views/portfolio/account-group-queries';
import { useHouseholdIncomeReport } from 'views/portfolio/reporting-queries';

import { getScopedWithdrawals } from '../income';
import { IncomeHero } from './IncomeHero';
import { PlannedWithdrawalsCard } from './PlannedWithdrawalsCard';
import { ProjectedIncomeCard } from './ProjectedIncomeCard';
import { TrailingIncomeCard } from './TrailingIncomeCard';

interface IncomeSectionProps {
    householdGUID: string;
    selectedAccountGUIDs: string[];
    includeClosed: boolean;
}

// The Income & Cash Flow section. Income comes from the income report; planned withdrawals from
// the Investment Policy withdrawal plans of the household's account groups, narrowed to the same
// account scope.
export const IncomeSection = ({ householdGUID, selectedAccountGUIDs, includeClosed }: IncomeSectionProps) => {
    const { data: report, isPending } = useHouseholdIncomeReport({
        householdGUID,
        groupByGuids: selectedAccountGUIDs,
        includeClosed,
    });
    // Same query as ReportingHeader's account selector, so it is served from cache.
    const profilesQuery = useListAccountGroupProfilesWithAumIncluded(householdGUID);
    const profiles = profilesQuery.data ?? [];
    const cashFlowConfigsQuery = useListCashflowConfigsForAccountGroups(
        profiles.map((profile) => profile.accountGroup.accountGroupGUID),
    );
    const withdrawals = getScopedWithdrawals(profiles, cashFlowConfigsQuery.data, selectedAccountGUIDs, includeClosed);
    const isWithdrawalsPending = profilesQuery.isPending || cashFlowConfigsQuery.isPending;
    // A failed request must not read as "no withdrawals", so the withdrawal figures show as unavailable.
    const isWithdrawalsError = profilesQuery.isError || cashFlowConfigsQuery.isError;

    return (
        <>
            <IncomeHero
                report={report}
                withdrawals={withdrawals}
                isWithdrawalsError={isWithdrawalsError}
                isLoading={isPending || isWithdrawalsPending}
            />
            {isPending ? (
                <>
                    <div className="bg-cocoa-100 h-52 w-full animate-pulse rounded-2xl" />
                    <div className="bg-cocoa-100 h-72 w-full animate-pulse rounded-2xl" />
                </>
            ) : report ? (
                <>
                    <TrailingIncomeCard income={report.previousTwelveMonths} />
                    <ProjectedIncomeCard projectedIncome={report.projectedIncome} />
                </>
            ) : (
                // The endpoint answers 204 until the household has an income projection.
                <p className="text-cocoa-400 text-sm">No income projection for these accounts yet.</p>
            )}
            {/* Withdrawals come from the Investment Policy, so they show with or without an income projection. */}
            <PlannedWithdrawalsCard
                withdrawals={withdrawals}
                accounts={profiles.flatMap((profile) => profile.accounts)}
                isLoading={isWithdrawalsPending}
                isError={isWithdrawalsError}
            />
        </>
    );
};
